import { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { ALL_BODY_IDS, type BodyId } from "@solar/ephemeris";
import { resolveQuality, useScene } from "../store";
import { sunRelUniform } from "./frame";
import { Background } from "./Background";
import { Body, Sun } from "./Body";
import { CameraRig } from "./CameraRig";
import { OrbitLine } from "./Orbits";
import { FrameUpdater } from "./FrameUpdater";
import { frame } from "./frame";
import { OverlayDriver, OverlayView } from "./Overlay";
import { Comet } from "./Comet";
import { ParticleBelt } from "./SmallBodies";

const ORBIT_IDS = ALL_BODY_IDS.filter((id) => id !== "sun");

function Clock() {
  useFrame((_, dt) => useScene.getState().tick(Math.min(dt, 0.1)));
  return null;
}

/** 太阳光源与环境光：位置随浮动原点平移；真实比例下关闭“教学补光”（夜面近乎全黑）。 */
function Lights() {
  const sun = useRef<THREE.PointLight>(null), amb = useRef<THREE.AmbientLight>(null);
  useFrame(() => {
    sunRelUniform.value.copy(frame.sunRel);
    if (sun.current) sun.current.position.copy(frame.sunRel);
    if (amb.current) amb.current.intensity = 0.12 * (1 - frame.m) + 0.006 * frame.m;
  });
  // 太阳是唯一的光源：不随距离衰减，所以各行星的亮度只由朝向决定
  return (<><pointLight ref={sun} intensity={3.2} decay={0} distance={0} /><ambientLight ref={amb} intensity={0.12} /></>);
}

/** 天空球/星野始终以相机为中心（视觉上在无穷远）。 */
function SkyAnchor({ children }: { children: React.ReactNode }) {
  const g = useRef<THREE.Group>(null);
  useFrame(({ camera }) => { g.current?.position.copy(camera.position); });
  return <group ref={g}>{children}</group>;
}

export function SolarScene() {
  const q = resolveQuality(useScene((s) => s.quality));
  const dprMax = q === "low" ? 1 : q === "medium" ? 1.5 : 2;
  return (
    <div id="scene3d">
      <Canvas
        camera={{ fov: 40, near: 0.3, far: 20000, position: [200, 200, 250] }}
        dpr={[1, dprMax]}
        gl={{ antialias: true, powerPreference: "high-performance", logarithmicDepthBuffer: true }}
        onCreated={({ gl }) => { gl.toneMapping = THREE.NoToneMapping; }}
      >
        <color attach="background" args={["#02030a"]} />
        <FrameUpdater />
        <Clock />
        <CameraRig />
        <SkyAnchor><Background /></SkyAnchor>
        <Lights />
        <Sun />
        {ORBIT_IDS.map((id: BodyId) => (id === "halley" ? <Comet key={id} /> : <Body key={id} id={id} />))}
        <ParticleBelt belt="belt" />
        <ParticleBelt belt="kuiper" />
        {ORBIT_IDS.map((id: BodyId) => <OrbitLine key={`o-${id}`} id={id} />)}
        <OverlayDriver />
      </Canvas>
      <OverlayView />
    </div>
  );
}
